import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { includes } from 'lodash';
import themeHelpers from 'utils/theme-helpers';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';

const AVAILABLE_COLORS = ['default', 'green', 'primary', 'info', 'success', 'warning', 'danger'];
const AVAILABLE_SIZES = ['xs', 'sm', 'md', 'lg'];

function Button({
    active,
    activeBackgroundColor,
    className,
    content,
    color,
    disabled,
    hideContentOnLoading,
    hoverColor,
    textColor,
    size,
    textHoverColor,
    font,
    fontSize,
    paddingY,
    paddingX,
    border,
    borderColor,
    rounded,
    shadow,
    loading,
    startIcon,
    endIcon,
    onClick,
    displayLabel,
    title,
})
{
    const isDisabled = (disabled || loading);
    const themed = includes(AVAILABLE_COLORS, color);
    const finalTextColor = themed ? 'dark:tw-text-gray-100 tw-text-gray-200' : themeHelpers.text_colors[textColor];
    const finalTextHoverColor = themed ? 'hover:tw-text-gray-100' : themeHelpers.hover_text_colors[textHoverColor];
    const finalFocusTextColor = themed ? 'focus:tw-text-gray-100' : themeHelpers.focus_text_colors[textHoverColor];
    const finalHoverColor = themed ? themeHelpers.darker_hover_colors[color] : themeHelpers.hover_colors[hoverColor];
    const finalFocusColor = themed ? themeHelpers.darker_focus_colors[color] : themeHelpers.focus_colors[hoverColor];
    const finalColor = color === 'default' ? 'transparent' : themeHelpers.bg_colors[color];
    const finalBgColor = !isDisabled ? finalColor : `${themeHelpers.lighter_bg_colors[color]} dark:tw-bg-zinc-700`;
    let finalPaddingX = paddingX;
    let finalPaddingY = paddingY;
    if (finalPaddingX === undefined) finalPaddingX = includes(AVAILABLE_SIZES, size) ? themeHelpers.padding[size].paddingX : themeHelpers.padding[size].lg;
    if (finalPaddingY === undefined) finalPaddingY = includes(AVAILABLE_SIZES, size) ? themeHelpers.padding[size].paddingY : themeHelpers.padding[size].lg;
    const finalFontSize = size === 'xs' ? themeHelpers.font_size[fontSize] : fontSize;
    const classes = clsx({
        [className]: !!className,
        [`${finalBgColor}`]: true,
        [`${finalHoverColor}`]: !isDisabled,
        [`${finalFocusColor}`]: !isDisabled,
        [`${finalTextColor}`]: true,
        [`${finalFontSize}`]: true,
        [`${finalTextHoverColor}`]: true,
        [`${finalFocusTextColor}`]: true,
        [`${themeHelpers.font[font]}`]: true,
        [`${finalPaddingY}`]: true,
        [`${finalPaddingX}`]: true,
        'tw-border': border,
        [`${themeHelpers.border_colors[borderColor]}`]: !themed,
        'tw-rounded': rounded,
        'tw-shadow': shadow,
        'tw-cursor-not-allowed': loading,
        [`${themeHelpers.bg_colors[activeBackgroundColor]}`]: active,
        [`${themeHelpers.hover_colors[activeBackgroundColor]}`]: active,
        [`${themeHelpers.focus_colors[activeBackgroundColor]}`]: active,
    });

    const style = { outline: 'none' };

    if (isDisabled) style.cursor = 'not-allowed';

    return (
        <button data-display-label={displayLabel} type="button" className={classes} style={style} onClick={onClick} disabled={isDisabled} title={title}>
            {startIcon}
            {!loading && startIcon ? ' ' : ''}
            {loading && hideContentOnLoading ? '' : content}
            {!loading && endIcon ? ' ' : ''}
            {!loading && endIcon }
            <LoadingSpinner loading={loading} className="tw-mr-1 tw-ml-1" />
        </button>
    );
}

Button.displayName = 'Button';

Button.defaultProps = {
    active: false,
    activeBackgroundColor: 'gray-500',
    border: true,
    borderColor: 'gray-400',
    color: 'default',
    endIcon: null,
    font: 'semibold',
    fontSize: 'base',
    hideContentOnLoading: false,
    hoverColor: 'gray-100',
    loading: false,
    rounded: true,
    size: 'md',
    shadow: false,
    startIcon: null,
    textColor: 'gray-800',
    textHoverColor: 'gray-800',
    onClick: () => null,
    title: '',
};

Button.propTypes = {
    active: PropTypes.bool,
    activeBackgroundColor: PropTypes.string,
    border: PropTypes.bool,
    borderColor: PropTypes.string,
    className: PropTypes.string,
    color: PropTypes.oneOf([...AVAILABLE_COLORS, 'custom']),
    disabled: PropTypes.bool,
    endIcon: PropTypes.node,
    font: PropTypes.string,
    fontSize: PropTypes.string,
    hideContentOnLoading: PropTypes.bool,
    hoverColor: PropTypes.string,
    loading: PropTypes.bool,
    paddingY: PropTypes.number,
    paddingX: PropTypes.number,
    rounded: PropTypes.bool,
    size: PropTypes.oneOf(AVAILABLE_SIZES),
    shadow: PropTypes.bool,
    startIcon: PropTypes.node,
    textColor: PropTypes.string,
    textHoverColor: PropTypes.string,
    content: PropTypes.node.isRequired,
    onClick: PropTypes.func,
    title: PropTypes.string,
    displayLabel: PropTypes.string,
};

export default Button;
